/**
 * combat/skills.ts — The Three Ember Arts
 *
 * Every soul in this world carries ember — that inner fire inherited from
 * the old gods. Ember Arts are how you shape it. Flame for wrath. Root for
 * endurance. Wind for cunning. No one tells you which tree to climb; you
 * just reach for the branches that feel right.
 *
 * Active abilities cost Ember Points (EP) and live on your skill bar (1-6).
 * Passive talents modify stats or proc effects and are always active once
 * unlocked. Weapon/gear abilities are granted by equipment and occupy their
 * own bar slots — you lose them when you unequip.
 *
 * Design philosophy: every ability should FEEL different. Different wind-up,
 * impact, sound, camera response. No two abilities should ever feel like
 * reskins of each other.
 */

import type { DamageType, EmberTree, BuffType } from "../engine/state.js";

// ─── Ability Definition ───────────────────────────────────────────────────

export type AbilityTarget = "enemy" | "self" | "aoe_around_self" | "aoe_at_target" | "cone";

export interface AbilityEffect {
  type: "damage" | "heal" | "buff" | "debuff" | "knockback" | "lifesteal" | "execute";
  damageType?: DamageType;
  buffType?: BuffType;
  /** Base value — scales with level and stats */
  baseValue: number;
  /** Stat scaling coefficient (e.g., 0.8 = 80% of attack power added) */
  scaling: number;
  /** Duration in seconds for buffs/debuffs (0 for instant) */
  duration: number;
  /** Tick interval for DoTs/HoTs (0 = no tick, instant effect) */
  tickInterval: number;
  /** Max stacks for stackable effects */
  maxStacks: number;
}

export interface AbilityVisual {
  /** Character animation to play ("slash", "thrust", "cast", "slam", "spin", "channel") */
  animation: string;
  /** Screen shake intensity on hit (0 = none) */
  shakeIntensity: number;
  /** Particle effect key */
  particles: string;
  /** Camera zoom pulse (1.0 = no zoom, 1.05 = subtle punch-in) */
  cameraPulse: number;
  /** Sound effect key */
  sound: string;
  /** Trail color for weapon swing arcs */
  trailColor: string;
  /** Flash color overlaid on target on hit */
  hitFlashColor: string;
  /** Duration of the wind-up before damage applies (seconds) */
  windupTime: number;
}

export interface AbilityDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;                      // glyph/symbol for the skill bar
  tree: EmberTree | "weapon" | "generic";
  tier: number;                      // 0 = starter, 1-3 = unlockable tiers
  /** EP cost to cast */
  cost: number;
  /** Cooldown in seconds */
  cooldown: number;
  /** Max charges (1 = normal, 2+ = multi-charge) */
  maxCharges: number;
  /** Charge recharge time in seconds (only matters if maxCharges > 1) */
  chargeRechargeTime: number;
  /** GCD triggered? (false = off-GCD, can be weaved) */
  triggersGCD: boolean;
  target: AbilityTarget;
  /** Radius for AoE abilities (in tiles) */
  aoeRadius: number;
  /** Effects applied in sequence */
  effects: AbilityEffect[];
  /** Visual/audio feedback */
  visual: AbilityVisual;
  /** Required player level to unlock (0 = available from start) */
  levelRequired: number;
  /** Prerequisite ability IDs in the same tree */
  prerequisites: string[];
  /** Flavor text shown in skill tree */
  lore: string;
}

// ─── Passive Talent Definition ────────────────────────────────────────────

export type PassiveModifier =
  | { stat: "attack"; flat: number; percent: number }
  | { stat: "defense"; flat: number; percent: number }
  | { stat: "maxHp"; flat: number; percent: number }
  | { stat: "maxEmber"; flat: number; percent: number }
  | { stat: "emberRegen"; flat: number; percent: number }
  | { stat: "critChance"; flat: number }
  | { stat: "critDamage"; flat: number }
  | { stat: "attackSpeed"; percent: number }
  | { stat: "damageReduction"; percent: number }
  | { stat: "lifesteal"; percent: number }
  | { stat: "thornsReflect"; percent: number }
  | { stat: "dotDamage"; percent: number }
  | { stat: "cooldownReduction"; percent: number }
  | { stat: "aoeRadius"; percent: number };

export interface PassiveProc {
  /** Trigger event */
  trigger: "on_hit" | "on_crit" | "on_kill" | "on_take_damage" | "on_dodge" | "on_ability_cast";
  /** Chance to proc (0.0 - 1.0) */
  chance: number;
  /** Effect to apply */
  effect: AbilityEffect;
  /** Internal cooldown in seconds */
  icd: number;
}

export interface PassiveTalent {
  id: string;
  name: string;
  description: string;
  icon: string;
  tree: EmberTree;
  tier: number;
  maxRanks: number;                  // 1-3 ranks per talent
  modifiers: PassiveModifier[];      // stat bonuses per rank
  procs: PassiveProc[];              // on-event effects
  prerequisites: string[];           // ability or talent IDs
  levelRequired: number;
  lore: string;
}

// ─── Helper to make visual objects less repetitive ────────────────────────

function vis(
  animation: string, sound: string, particles: string,
  shake: number, trail: string, flash: string,
  windup = 0.15, cameraPulse = 1.0,
): AbilityVisual {
  return { animation, sound, particles, shakeIntensity: shake, trailColor: trail, hitFlashColor: flash, windupTime: windup, cameraPulse };
}

function dmg(base: number, scaling: number, type: DamageType = "physical"): AbilityEffect {
  return { type: "damage", damageType: type, baseValue: base, scaling, duration: 0, tickInterval: 0, maxStacks: 1 };
}

function dot(base: number, scaling: number, type: DamageType, buffType: BuffType, dur: number, tick: number, stacks = 1): AbilityEffect {
  return { type: "debuff", damageType: type, buffType, baseValue: base, scaling, duration: dur, tickInterval: tick, maxStacks: stacks };
}

function buff(base: number, type: BuffType, dur: number, stacks = 1): AbilityEffect {
  return { type: "buff", buffType: type, baseValue: base, scaling: 0, duration: dur, tickInterval: 0, maxStacks: stacks };
}

function heal(base: number, scaling: number): AbilityEffect {
  return { type: "heal", damageType: "heal", baseValue: base, scaling, duration: 0, tickInterval: 0, maxStacks: 1 };
}

// ═══════════════════════════════════════════════════════════════════════════
//  FLAME TREE — Wrath of the Ember
//  Offense-focused: raw damage, crits, fire DoTs, execution
// ═══════════════════════════════════════════════════════════════════════════

export const FLAME_ABILITIES: AbilityDefinition[] = [
  // ── Tier 0: Starter ────────────────────────────────────────────────────
  {
    id: "flame_strike",
    name: "Ember Strike",
    description: "Channel ember into your weapon for a searing melee blow. Burns on crit.",
    icon: "🔥",
    tree: "flame", tier: 0,
    cost: 3, cooldown: 0, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [dmg(8, 1.0, "fire")],
    visual: vis("slash", "ability_flame_strike", "fire_slash", 4, "#FF6030", "#FF4400", 0.12, 1.02),
    levelRequired: 1, prerequisites: [],
    lore: "The simplest art: make your blade burn.",
  },
  // ── Tier 1 ─────────────────────────────────────────────────────────────
  {
    id: "flame_lash",
    name: "Flame Lash",
    description: "Whip a tendril of fire at a distant enemy. Applies Burn for 6s.",
    icon: "🪢",
    tree: "flame", tier: 1,
    cost: 5, cooldown: 6, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [
      dmg(6, 0.6, "fire"),
      dot(3, 0.3, "fire", "burn", 6, 2),
    ],
    visual: vis("cast", "ability_flame_lash", "fire_whip", 3, "#FF5020", "#FF3300", 0.2, 1.0),
    levelRequired: 3, prerequisites: ["flame_strike"],
    lore: "Ember doesn't just warm — it reaches.",
  },
  {
    id: "ignition",
    name: "Ignition",
    description: "Detonate all Burn stacks on the target for massive burst damage.",
    icon: "💥",
    tree: "flame", tier: 1,
    cost: 6, cooldown: 10, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [dmg(4, 1.5, "fire")],  // + bonus per burn stack in combat logic
    visual: vis("thrust", "ability_ignition", "fire_explosion", 8, "#FFAA20", "#FFFFFF", 0.08, 1.06),
    levelRequired: 5, prerequisites: ["flame_strike"],
    lore: "Every spark you planted — harvest them all at once.",
  },
  // ── Tier 2 ─────────────────────────────────────────────────────────────
  {
    id: "inferno_sweep",
    name: "Inferno Sweep",
    description: "Spin with a wave of flame, hitting all enemies within 2 tiles.",
    icon: "🌀",
    tree: "flame", tier: 2,
    cost: 10, cooldown: 14, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "aoe_around_self", aoeRadius: 2,
    effects: [dmg(12, 0.8, "fire")],
    visual: vis("spin", "ability_inferno_sweep", "fire_ring", 6, "#FF4400", "#FF8800", 0.25, 1.04),
    levelRequired: 8, prerequisites: ["flame_lash"],
    lore: "When surrounded, become the furnace.",
  },
  {
    id: "pyroclasm",
    name: "Pyroclasm",
    description: "Hurl a slow-burning ember that erupts after 1.5s, dealing massive AoE fire damage.",
    icon: "☄️",
    tree: "flame", tier: 2,
    cost: 12, cooldown: 18, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "aoe_at_target", aoeRadius: 2,
    effects: [dmg(20, 1.2, "fire")],
    visual: vis("cast", "ability_pyroclasm", "fire_meteor", 10, "#FF6600", "#FFCC00", 0.4, 1.08),
    levelRequired: 10, prerequisites: ["ignition"],
    lore: "Not every flame is in a hurry.",
  },
  // ── Tier 3: Ultimate ───────────────────────────────────────────────────
  {
    id: "phoenix_rage",
    name: "Phoenix Rage",
    description: "Become wreathed in flame for 8s. All attacks deal bonus fire damage, movement leaves burning ground, and you regenerate HP.",
    icon: "🦅",
    tree: "flame", tier: 3,
    cost: 18, cooldown: 60, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: false, target: "self", aoeRadius: 0,
    effects: [
      buff(0, "might", 8),
      { type: "heal", damageType: "heal", baseValue: 3, scaling: 0.2, duration: 8, tickInterval: 1, maxStacks: 1 },
    ],
    visual: vis("channel", "ability_phoenix_rage", "fire_aura", 4, "#FF8800", "#FFDD00", 0.5, 1.1),
    levelRequired: 15, prerequisites: ["inferno_sweep", "pyroclasm"],
    lore: "The phoenix does not fear the fire. The phoenix IS the fire.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  ROOT TREE — Heartwood's Embrace
//  Defense/support: shields, healing, thorns, crowd control
// ═══════════════════════════════════════════════════════════════════════════

export const ROOT_ABILITIES: AbilityDefinition[] = [
  // ── Tier 0: Starter ────────────────────────────────────────────────────
  {
    id: "bark_shield",
    name: "Bark Shield",
    description: "Wrap yourself in living bark. Absorbs damage equal to 20% of max HP for 10s.",
    icon: "🛡️",
    tree: "root", tier: 0,
    cost: 4, cooldown: 12, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "self", aoeRadius: 0,
    effects: [buff(15, "shield", 10)],
    visual: vis("channel", "ability_bark_shield", "nature_shield", 2, "#558844", "#88BB66", 0.3, 1.0),
    levelRequired: 1, prerequisites: [],
    lore: "The oldest trees don't dodge storms — they endure them.",
  },
  // ── Tier 1 ─────────────────────────────────────────────────────────────
  {
    id: "thorn_mantle",
    name: "Thorn Mantle",
    description: "Sprout thorns for 12s. Attackers take nature damage on each melee hit against you.",
    icon: "🌿",
    tree: "root", tier: 1,
    cost: 5, cooldown: 16, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "self", aoeRadius: 0,
    effects: [buff(4, "thorns", 12)],
    visual: vis("channel", "ability_thorn_mantle", "nature_thorns", 2, "#44AA33", "#66DD44", 0.25, 1.0),
    levelRequired: 3, prerequisites: ["bark_shield"],
    lore: "Kindness has limits. So do roots.",
  },
  {
    id: "mending_sap",
    name: "Mending Sap",
    description: "Draw life from the earth, healing over 6s.",
    icon: "💚",
    tree: "root", tier: 1,
    cost: 6, cooldown: 8, maxCharges: 2, chargeRechargeTime: 10,
    triggersGCD: true, target: "self", aoeRadius: 0,
    effects: [{ type: "heal", damageType: "heal", baseValue: 5, scaling: 0.4, duration: 6, tickInterval: 1.5, maxStacks: 1 }],
    visual: vis("channel", "ability_mending_sap", "nature_heal", 0, "#44CC44", "#88FF88", 0.35, 1.0),
    levelRequired: 4, prerequisites: ["bark_shield"],
    lore: "The sap remembers the sun. Let it remember you.",
  },
  // ── Tier 2 ─────────────────────────────────────────────────────────────
  {
    id: "ironroot_stance",
    name: "Ironroot Stance",
    description: "Root yourself in place for 6s. Cannot move but gain massive defense and reflect damage.",
    icon: "🌳",
    tree: "root", tier: 2,
    cost: 8, cooldown: 20, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: false, target: "self", aoeRadius: 0,
    effects: [
      buff(10, "fortify", 6),
      buff(8, "thorns", 6),
    ],
    visual: vis("channel", "ability_ironroot", "nature_roots", 3, "#335522", "#558844", 0.4, 1.0),
    levelRequired: 8, prerequisites: ["thorn_mantle"],
    lore: "Plant your feet. Let them come.",
  },
  {
    id: "entangle",
    name: "Entangle",
    description: "Vines erupt from the ground, stunning the target for 2s and applying poison.",
    icon: "🕸️",
    tree: "root", tier: 2,
    cost: 7, cooldown: 15, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [
      dot(0, 0, "nature", "stun", 2, 0),
      dot(4, 0.3, "nature", "poison", 8, 2, 3),
    ],
    visual: vis("cast", "ability_entangle", "nature_vines", 4, "#226622", "#44AA22", 0.3, 1.02),
    levelRequired: 10, prerequisites: ["mending_sap"],
    lore: "The forest floor remembers every trespass.",
  },
  // ── Tier 3: Ultimate ───────────────────────────────────────────────────
  {
    id: "world_tree",
    name: "World Tree",
    description: "Become an avatar of the World Tree for 10s. Massive HP regen, all damage reduced by 50%, and thorns reflect triple damage.",
    icon: "🌍",
    tree: "root", tier: 3,
    cost: 20, cooldown: 90, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: false, target: "self", aoeRadius: 0,
    effects: [
      { type: "heal", damageType: "heal", baseValue: 8, scaling: 0.5, duration: 10, tickInterval: 1, maxStacks: 1 },
      buff(50, "fortify", 10),
      buff(12, "thorns", 10),
    ],
    visual: vis("channel", "ability_world_tree", "nature_avatar", 5, "#228822", "#66FF66", 0.6, 1.12),
    levelRequired: 15, prerequisites: ["ironroot_stance", "entangle"],
    lore: "For ten heartbeats, you are the forest. Every root. Every branch. Every thorn.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  WIND TREE — Breath of the Wayward
//  Utility: speed, evasion, frost/shadow, debuffs, mobility
// ═══════════════════════════════════════════════════════════════════════════

export const WIND_ABILITIES: AbilityDefinition[] = [
  // ── Tier 0: Starter ────────────────────────────────────────────────────
  {
    id: "gust_slash",
    name: "Gust Slash",
    description: "A wind-sharpened strike that ignores 30% of enemy armor.",
    icon: "💨",
    tree: "wind", tier: 0,
    cost: 3, cooldown: 0, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [dmg(6, 0.9, "frost")],
    visual: vis("slash", "ability_gust_slash", "wind_slash", 3, "#88CCFF", "#AADDFF", 0.1, 1.01),
    levelRequired: 1, prerequisites: [],
    lore: "The wind cuts where armor doesn't cover.",
  },
  // ── Tier 1 ─────────────────────────────────────────────────────────────
  {
    id: "frost_brand",
    name: "Frost Brand",
    description: "Mark the enemy with frost. Slows them and increases damage they take by 15% for 8s.",
    icon: "❄️",
    tree: "wind", tier: 1,
    cost: 5, cooldown: 10, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [
      dot(0, 0, "frost", "slow", 8, 0),
      dot(0, 0, "frost", "shadow_mark", 8, 0),
    ],
    visual: vis("cast", "ability_frost_brand", "frost_mark", 2, "#66AAFF", "#88CCFF", 0.2, 1.0),
    levelRequired: 3, prerequisites: ["gust_slash"],
    lore: "Cold makes everything brittle.",
  },
  {
    id: "shadow_step",
    name: "Shadow Step",
    description: "Teleport behind the target, becoming invisible for 1.5s. Next attack from stealth crits.",
    icon: "👤",
    tree: "wind", tier: 1,
    cost: 6, cooldown: 14, maxCharges: 2, chargeRechargeTime: 18,
    triggersGCD: false, target: "enemy", aoeRadius: 0,
    effects: [buff(0, "haste", 1.5)],  // handled specially in combat — guarantees next crit
    visual: vis("cast", "ability_shadow_step", "shadow_teleport", 0, "#443366", "#7755AA", 0.05, 1.0),
    levelRequired: 5, prerequisites: ["gust_slash"],
    lore: "You don't see the wind move. You only feel where it was.",
  },
  // ── Tier 2 ─────────────────────────────────────────────────────────────
  {
    id: "blizzard_veil",
    name: "Blizzard Veil",
    description: "Summon a frost storm around you for 6s. Enemies within 2 tiles take frost damage every second and are slowed.",
    icon: "🌨️",
    tree: "wind", tier: 2,
    cost: 10, cooldown: 18, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "aoe_around_self", aoeRadius: 2,
    effects: [
      dot(5, 0.4, "frost", "frostbite", 6, 1),
      dot(0, 0, "frost", "slow", 6, 0),
    ],
    visual: vis("channel", "ability_blizzard_veil", "frost_storm", 3, "#6699DD", "#AACCFF", 0.35, 1.03),
    levelRequired: 8, prerequisites: ["frost_brand"],
    lore: "The cold doesn't chase you. It simply waits.",
  },
  {
    id: "phantom_blade",
    name: "Phantom Blade",
    description: "Launch a shadow blade that pierces through the target, dealing shadow damage and reducing their healing by 50% for 6s.",
    icon: "🗡️",
    tree: "wind", tier: 2,
    cost: 7, cooldown: 12, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [
      dmg(14, 1.1, "shadow"),
      dot(0, 0, "shadow", "shadow_mark", 6, 0),
    ],
    visual: vis("thrust", "ability_phantom_blade", "shadow_blade", 5, "#553388", "#8844CC", 0.15, 1.03),
    levelRequired: 10, prerequisites: ["shadow_step"],
    lore: "Shadows don't wound the body. They wound what's underneath.",
  },
  // ── Tier 3: Ultimate ───────────────────────────────────────────────────
  {
    id: "storm_sovereign",
    name: "Storm Sovereign",
    description: "Command wind and shadow for 10s. Gain 40% attack speed, 30% dodge chance, and leave afterimages that confuse enemies.",
    icon: "⚡",
    tree: "wind", tier: 3,
    cost: 18, cooldown: 75, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: false, target: "self", aoeRadius: 0,
    effects: [
      buff(40, "haste", 10),
    ],
    visual: vis("channel", "ability_storm_sovereign", "wind_aura", 4, "#7799EE", "#AABBFF", 0.5, 1.1),
    levelRequired: 15, prerequisites: ["blizzard_veil", "phantom_blade"],
    lore: "Become the storm. Not its master — its body.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  GENERIC ABILITIES — Available to all players regardless of tree
// ═══════════════════════════════════════════════════════════════════════════

export const GENERIC_ABILITIES: AbilityDefinition[] = [
  {
    id: "second_wind",
    name: "Second Wind",
    description: "Catch your breath, recovering 25% of max HP over 4s. Can't be used above 50% HP.",
    icon: "💛",
    tree: "generic", tier: 0,
    cost: 8, cooldown: 30, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: false, target: "self", aoeRadius: 0,
    effects: [{ type: "heal", damageType: "heal", baseValue: 10, scaling: 0.5, duration: 4, tickInterval: 1, maxStacks: 1 }],
    visual: vis("channel", "ability_second_wind", "heal_glow", 0, "#FFDD66", "#FFEEAA", 0.3, 1.0),
    levelRequired: 2, prerequisites: [],
    lore: "Even ember needs to breathe.",
  },
  {
    id: "war_cry",
    name: "War Cry",
    description: "Let out a battle shout, gaining 20% attack power for 10s.",
    icon: "📢",
    tree: "generic", tier: 0,
    cost: 4, cooldown: 20, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: false, target: "self", aoeRadius: 0,
    effects: [buff(0, "might", 10)],
    visual: vis("channel", "ability_war_cry", "shout_ring", 2, "#DDAA44", "#FFCC66", 0.2, 1.02),
    levelRequired: 3, prerequisites: [],
    lore: "Volume is its own kind of armor.",
  },
  {
    id: "focused_strike",
    name: "Focused Strike",
    description: "A precise, deliberate blow. Guaranteed critical hit. Long cooldown.",
    icon: "🎯",
    tree: "generic", tier: 0,
    cost: 6, cooldown: 16, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [dmg(5, 1.3, "physical")],
    visual: vis("thrust", "ability_focused_strike", "precision_flash", 6, "#FFFFFF", "#FFFFAA", 0.2, 1.04),
    levelRequired: 4, prerequisites: [],
    lore: "One swing. Make it count.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  WEAPON-GRANTED ABILITIES — Gained/lost with equipment
// ═══════════════════════════════════════════════════════════════════════════

export const WEAPON_ABILITIES: AbilityDefinition[] = [
  // Sword
  {
    id: "weapon_riposte",
    name: "Riposte",
    description: "Parry the next attack within 2s and counter with a precise thrust.",
    icon: "⚔️",
    tree: "weapon", tier: 0,
    cost: 4, cooldown: 10, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: false, target: "self", aoeRadius: 0,
    effects: [buff(0, "shield", 2)],  // special: next incoming attack is reflected
    visual: vis("slash", "ability_riposte", "parry_flash", 3, "#C8D0D8", "#FFFFFF", 0.1, 1.02),
    levelRequired: 0, prerequisites: [],
    lore: "The best defense is to make them regret attacking.",
  },
  // Axe
  {
    id: "weapon_cleave",
    name: "Cleave",
    description: "A brutal horizontal swing hitting all enemies in a wide arc.",
    icon: "🪓",
    tree: "weapon", tier: 0,
    cost: 5, cooldown: 8, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "aoe_around_self", aoeRadius: 1.5,
    effects: [dmg(10, 0.9, "physical")],
    visual: vis("spin", "ability_cleave", "arc_slash", 5, "#A8A8A0", "#CCCCCC", 0.18, 1.03),
    levelRequired: 0, prerequisites: [],
    lore: "Why hit one when you can hit all of them?",
  },
  // Staff
  {
    id: "weapon_arcane_bolt",
    name: "Arcane Bolt",
    description: "Fire a bolt of concentrated ember energy at range.",
    icon: "✨",
    tree: "weapon", tier: 0,
    cost: 3, cooldown: 4, maxCharges: 2, chargeRechargeTime: 5,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [dmg(7, 0.8, "fire")],
    visual: vis("cast", "ability_arcane_bolt", "arcane_projectile", 2, "#A0E0FF", "#CCFFFF", 0.15, 1.01),
    levelRequired: 0, prerequisites: [],
    lore: "The staff remembers every spell it has channeled.",
  },
  // Mace
  {
    id: "weapon_shatter",
    name: "Shatter",
    description: "A devastating overhead slam that stuns for 1.5s and reduces armor.",
    icon: "🔨",
    tree: "weapon", tier: 0,
    cost: 6, cooldown: 12, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [
      dmg(12, 1.0, "physical"),
      dot(0, 0, "physical", "stun", 1.5, 0),
    ],
    visual: vis("slam", "ability_shatter", "ground_crack", 8, "#909090", "#FFFFFF", 0.25, 1.06),
    levelRequired: 0, prerequisites: [],
    lore: "Subtlety is overrated.",
  },
  // Dagger
  {
    id: "weapon_lacerate",
    name: "Lacerate",
    description: "Rapid strikes that stack a bleed effect. Up to 5 stacks.",
    icon: "🔪",
    tree: "weapon", tier: 0,
    cost: 3, cooldown: 3, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [dot(2, 0.2, "physical", "poison", 8, 2, 5)],
    visual: vis("slash", "ability_lacerate", "bleed_slash", 2, "#CC2222", "#FF4444", 0.08, 1.01),
    levelRequired: 0, prerequisites: [],
    lore: "Death by a thousand cuts starts somewhere.",
  },
  // Two-handed sword
  {
    id: "weapon_executioner",
    name: "Executioner's Verdict",
    description: "Massive overhead swing. Deals 50% bonus damage to targets below 30% HP.",
    icon: "⚰️",
    tree: "weapon", tier: 0,
    cost: 8, cooldown: 14, maxCharges: 1, chargeRechargeTime: 0,
    triggersGCD: true, target: "enemy", aoeRadius: 0,
    effects: [{ type: "execute", damageType: "physical", baseValue: 18, scaling: 1.4, duration: 0, tickInterval: 0, maxStacks: 1 }],
    visual: vis("slam", "ability_executioner", "heavy_slash", 10, "#DD3333", "#FF6666", 0.35, 1.08),
    levelRequired: 0, prerequisites: [],
    lore: "The last thing they hear is the weight of the blade.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  PASSIVE TALENTS — Permanent upgrades in each tree
// ═══════════════════════════════════════════════════════════════════════════

export const FLAME_PASSIVES: PassiveTalent[] = [
  {
    id: "flame_p_kindling",
    name: "Kindling",
    description: "+5% critical strike chance per rank.",
    icon: "🕯️", tree: "flame", tier: 1, maxRanks: 3,
    modifiers: [{ stat: "critChance", flat: 0.05 }],
    procs: [],
    prerequisites: ["flame_strike"], levelRequired: 2,
    lore: "Small sparks, carefully tended.",
  },
  {
    id: "flame_p_accelerant",
    name: "Accelerant",
    description: "Fire DoTs deal 15% more damage per rank.",
    icon: "🧪", tree: "flame", tier: 2, maxRanks: 2,
    modifiers: [{ stat: "dotDamage", percent: 0.15 }],
    procs: [],
    prerequisites: ["flame_lash"], levelRequired: 6,
    lore: "The right fuel turns flame into inferno.",
  },
  {
    id: "flame_p_immolation",
    name: "Immolation Aura",
    description: "Killing a burning enemy has a 30% chance to apply Burn to nearby enemies.",
    icon: "🔥", tree: "flame", tier: 2, maxRanks: 1,
    modifiers: [],
    procs: [{
      trigger: "on_kill", chance: 0.3, icd: 2,
      effect: dot(3, 0.2, "fire", "burn", 6, 2),
    }],
    prerequisites: ["flame_lash"], levelRequired: 7,
    lore: "Some fires spread on their own.",
  },
  {
    id: "flame_p_pyromaniac",
    name: "Pyromaniac",
    description: "Critical hits with fire abilities restore 2 EP.",
    icon: "🌋", tree: "flame", tier: 3, maxRanks: 1,
    modifiers: [],
    procs: [{
      trigger: "on_crit", chance: 1.0, icd: 1,
      effect: { type: "heal", damageType: "heal", baseValue: 0, scaling: 0, duration: 0, tickInterval: 0, maxStacks: 1 },
      // EP restoration handled in combat logic
    }],
    prerequisites: ["flame_p_accelerant"], levelRequired: 12,
    lore: "Destruction fuels creation. Or at least, more destruction.",
  },
];

export const ROOT_PASSIVES: PassiveTalent[] = [
  {
    id: "root_p_thick_bark",
    name: "Thick Bark",
    description: "+3% damage reduction per rank.",
    icon: "🪵", tree: "root", tier: 1, maxRanks: 3,
    modifiers: [{ stat: "damageReduction", percent: 0.03 }],
    procs: [],
    prerequisites: ["bark_shield"], levelRequired: 2,
    lore: "Layer by layer, the bark thickens.",
  },
  {
    id: "root_p_sap_blood",
    name: "Sap Blood",
    description: "2% of damage dealt is returned as healing per rank.",
    icon: "🩸", tree: "root", tier: 2, maxRanks: 3,
    modifiers: [{ stat: "lifesteal", percent: 0.02 }],
    procs: [],
    prerequisites: ["mending_sap"], levelRequired: 5,
    lore: "The tree drinks deep.",
  },
  {
    id: "root_p_spite_thorns",
    name: "Spite Thorns",
    description: "When hit, 20% chance to apply Poison to the attacker for 4s.",
    icon: "🌵", tree: "root", tier: 2, maxRanks: 1,
    modifiers: [],
    procs: [{
      trigger: "on_take_damage", chance: 0.2, icd: 3,
      effect: dot(3, 0.2, "nature", "poison", 4, 1, 3),
    }],
    prerequisites: ["thorn_mantle"], levelRequired: 6,
    lore: "Touch the thorns, learn the lesson.",
  },
  {
    id: "root_p_deeproot",
    name: "Deeproot Vitality",
    description: "+8% maximum HP per rank.",
    icon: "❤️", tree: "root", tier: 3, maxRanks: 2,
    modifiers: [{ stat: "maxHp", flat: 0, percent: 0.08 }],
    procs: [],
    prerequisites: ["root_p_sap_blood"], levelRequired: 10,
    lore: "The deepest roots survive the worst storms.",
  },
];

export const WIND_PASSIVES: PassiveTalent[] = [
  {
    id: "wind_p_fleet_foot",
    name: "Fleet Foot",
    description: "+8% attack speed per rank.",
    icon: "👟", tree: "wind", tier: 1, maxRanks: 3,
    modifiers: [{ stat: "attackSpeed", percent: 0.08 }],
    procs: [],
    prerequisites: ["gust_slash"], levelRequired: 2,
    lore: "Speed is the best armor.",
  },
  {
    id: "wind_p_bite",
    name: "Winter's Bite",
    description: "Frost abilities have a 15% chance to freeze the target for 1s.",
    icon: "🥶", tree: "wind", tier: 2, maxRanks: 1,
    modifiers: [],
    procs: [{
      trigger: "on_hit", chance: 0.15, icd: 5,
      effect: dot(0, 0, "frost", "stun", 1, 0),
    }],
    prerequisites: ["frost_brand"], levelRequired: 6,
    lore: "Cold enough to stop time.",
  },
  {
    id: "wind_p_shadow_feed",
    name: "Shadow Feed",
    description: "Killing an enemy restores 5 EP.",
    icon: "🌑", tree: "wind", tier: 2, maxRanks: 1,
    modifiers: [],
    procs: [{
      trigger: "on_kill", chance: 1.0, icd: 0,
      effect: { type: "heal", damageType: "heal", baseValue: 0, scaling: 0, duration: 0, tickInterval: 0, maxStacks: 1 },
      // EP restoration handled in combat logic
    }],
    prerequisites: ["shadow_step"], levelRequired: 7,
    lore: "Every ending feeds a beginning.",
  },
  {
    id: "wind_p_evasion",
    name: "Spectral Evasion",
    description: "10% cooldown reduction per rank on Wind abilities.",
    icon: "👻", tree: "wind", tier: 3, maxRanks: 2,
    modifiers: [{ stat: "cooldownReduction", percent: 0.10 }],
    procs: [],
    prerequisites: ["wind_p_bite", "wind_p_shadow_feed"], levelRequired: 11,
    lore: "Ghosts don't wait.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  WEAPON → ABILITY MAPPING
// ═══════════════════════════════════════════════════════════════════════════

/** Maps weapon class to the ability IDs that equipment grants */
export const WEAPON_CLASS_ABILITIES: Record<string, string[]> = {
  "sword_1h": ["weapon_riposte"],
  "sword_2h": ["weapon_executioner"],
  "axe":      ["weapon_cleave"],
  "staff":    ["weapon_arcane_bolt"],
  "mace":     ["weapon_shatter"],
  "dagger":   ["weapon_lacerate"],
};

// ═══════════════════════════════════════════════════════════════════════════
//  LOOKUP UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

/** All active abilities indexed by ID for O(1) lookup */
const ALL_ABILITIES: Map<string, AbilityDefinition> = new Map();
const ALL_PASSIVES: Map<string, PassiveTalent> = new Map();

// Build lookup maps
for (const list of [FLAME_ABILITIES, ROOT_ABILITIES, WIND_ABILITIES, GENERIC_ABILITIES, WEAPON_ABILITIES]) {
  for (const a of list) ALL_ABILITIES.set(a.id, a);
}
for (const list of [FLAME_PASSIVES, ROOT_PASSIVES, WIND_PASSIVES]) {
  for (const p of list) ALL_PASSIVES.set(p.id, p);
}

export function getAbility(id: string): AbilityDefinition | undefined {
  return ALL_ABILITIES.get(id);
}

export function getPassive(id: string): PassiveTalent | undefined {
  return ALL_PASSIVES.get(id);
}

/** Get all abilities for a specific tree */
export function getTreeAbilities(tree: EmberTree): AbilityDefinition[] {
  switch (tree) {
    case "flame": return FLAME_ABILITIES;
    case "root":  return ROOT_ABILITIES;
    case "wind":  return WIND_ABILITIES;
  }
}

/** Get all passives for a specific tree */
export function getTreePassives(tree: EmberTree): PassiveTalent[] {
  switch (tree) {
    case "flame": return FLAME_PASSIVES;
    case "root":  return ROOT_PASSIVES;
    case "wind":  return WIND_PASSIVES;
  }
}

/** Get abilities granted by a weapon class */
export function getWeaponAbilities(weaponClass: string): AbilityDefinition[] {
  const ids = WEAPON_CLASS_ABILITIES[weaponClass] ?? [];
  return ids.map(id => ALL_ABILITIES.get(id)).filter((a): a is AbilityDefinition => !!a);
}

/** Check if a player can unlock an ability based on level and prerequisites */
export function canUnlockAbility(
  abilityId: string,
  playerLevel: number,
  unlockedIds: Set<string>,
): boolean {
  const ability = ALL_ABILITIES.get(abilityId) ?? ALL_PASSIVES.get(abilityId);
  if (!ability) return false;
  if (playerLevel < ability.levelRequired) return false;
  return ability.prerequisites.every(preReq => unlockedIds.has(preReq));
}
